import { API_BASE } from "./util";

export interface InvitePosition {
  id: string;
  name: string;
}

export interface InviteSummary {
  inviteId: string;
  name: string;
  email: string;
  position?: InvitePosition;
  status: string;
  expiresAt: number;
}

export interface CreateInviteResponse {
  inviteId: string;
}

export async function createInvite(
  getToken: () => Promise<string | null>,
  orgId: string,
  body: { name: string; email: string; positionId: string; expiresAt: number },
): Promise<CreateInviteResponse> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/organizations/${orgId}/invite`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Failed to create invite: ${res.status}`);
  }
  return res.json();
}

export async function listInvites(
  getToken: () => Promise<string | null>,
  orgId: string,
): Promise<{ invites: InviteSummary[] }> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/organizations/${orgId}/invites`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Failed to list invites: ${res.status}`);
  }
  return res.json();
}

export async function deleteInvite(
  getToken: () => Promise<string | null>,
  orgId: string,
  inviteId: string,
): Promise<void> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/organizations/${orgId}/invite`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ inviteId }),
  });
  if (!res.ok) {
    throw new Error(`Failed to delete invite: ${res.status}`);
  }
}

export interface InvitePreview {
  organizationId: string;
  organizationName: string;
  positionName?: string;
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function getInvitePreview(
  getToken: () => Promise<string | null>,
  code: string,
): Promise<InvitePreview> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/invite/preview?code=${encodeURIComponent(code)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new HttpError(res.status, `Failed to preview invite: ${res.status}`);
  }
  return res.json();
}

export async function resolveInvite(
  getToken: () => Promise<string | null>,
  code: string,
  accept: boolean,
): Promise<void> {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/invite/resolve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ code, accept }),
  });
  if (!res.ok) {
    throw new Error(`Failed to resolve invite: ${res.status}`);
  }
}
